import { useEffect, useState } from "react";
import { Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { BottomSheet, ChalkActionButton } from "./primitives";

/**
 * EditAthleteSheet — opened from the roster card "edit" tap.
 *
 * Edits the 4 mutable fields (first/last/jersey/position) via PATCH, and
 * offers a soft-delete via DELETE with a one-tap confirmation. Guardians,
 * invites, and RSVP history all stay on the athlete row so we never lose
 * historical context when a coach tidies up the squad.
 */
export default function EditAthleteSheet({ isOpen, onDismiss, team, athlete, onSaved, onRemoved }) {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [jersey, setJersey] = useState("");
  const [position, setPosition] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (athlete) {
      setFirst(athlete.first_name || "");
      setLast(athlete.last_name || "");
      setJersey(athlete.jersey || "");
      setPosition(athlete.position || "");
      setConfirmDelete(false);
    }
  }, [athlete]);

  if (!athlete || !team) return null;

  async function save() {
    if (!first.trim() || !last.trim()) {
      toast.error("First and last name are required.");
      return;
    }
    setSaving(true);
    try {
      const data = await api.updateAthlete(team.id, athlete.id, {
        first_name: first.trim(),
        last_name: last.trim(),
        jersey: jersey.trim(),
        position: position.trim(),
      });
      toast.success(`Saved ${data.athlete.first_name}.`);
      onSaved && onSaved(data.athlete);
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setRemoving(true);
    try {
      await api.removeAthlete(team.id, athlete.id);
      toast.success(`${athlete.first_name} removed.`);
      onRemoved && onRemoved(athlete.id);
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't remove player.");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title={`Edit ${athlete.first_name}`} onDismiss={onDismiss}>
      <div data-testid="edit-athlete-sheet" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Field label="First name" value={first} onChange={setFirst} testId="edit-athlete-first" autoFocus />
          <Field label="Last name" value={last} onChange={setLast} testId="edit-athlete-last" />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8 }}>
          <Field label="Jersey #" value={jersey} onChange={setJersey} testId="edit-athlete-jersey" />
          <Field label="Position" value={position} onChange={setPosition} testId="edit-athlete-position" />
        </div>
        <ChalkActionButton
          testId="edit-athlete-save"
          variant="primary"
          label={saving ? "Saving…" : "Save changes"}
          icon={<Save size={16} aria-hidden="true" />}
          loading={saving}
          onClick={save}
        />
        <button
          type="button"
          onClick={remove}
          disabled={removing}
          data-testid="edit-athlete-remove"
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "11px 14px", borderRadius: 10,
            background: confirmDelete ? "#B02020" : "transparent",
            color: confirmDelete ? "#fff" : "#B02020",
            border: confirmDelete ? "none" : "0.5px solid rgba(176,32,32,0.35)",
            fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 12,
            letterSpacing: "0.08em", textTransform: "uppercase",
            cursor: removing ? "wait" : "pointer",
          }}
        >
          <Trash2 size={14} aria-hidden="true" />
          {confirmDelete ? "Tap again to confirm" : "Remove from roster"}
        </button>
      </div>
    </BottomSheet>
  );
}

function Field({ label, value, onChange, testId, autoFocus }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>
        {label}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        data-testid={testId}
        style={{
          width: "100%", border: "0.5px solid var(--chalk-border)",
          borderRadius: 10, padding: "11px 12px", fontSize: 16,
          fontFamily: "inherit", outline: "none",
          background: "#FBF8F2", color: "var(--chalk-board)",
        }}
      />
    </label>
  );
}
